import { useMemo } from 'react';

import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { logger } from '@/modules/common/logging/logger';

/**
 * @description A bridge/cross-store selector that determines if the currently loaded
 * revision matches the latest revision of the active layout alternative.
 *
 * --
 *
 * ARCHITECTURAL WARNING:
 *
 * This selector is strictly intended for the UI Layer (components/hooks).
 * Avoid using the `get()` or `useReactive()` methods inside services or non-UI layer code
 * to prevent circular dependencies and "leaky" responsibilities. For service-layer logic,
 * the pure `logic()` is ok to use, but be weary of this Selector its dependencies that you are dragging into the consumer.
 */
export const IsLatestRevisionSelector = {
  /**
   * @description Core logic of the selector
   */
  logic(layoutAlternativeLastRevisionId: string, revisionId: string) {
    return layoutAlternativeLastRevisionId === revisionId;
  },

  /**
   * @description Reactive hook for use where reactivity is desired
   */
  useReactive: (): boolean => {
    const activeLayoutAlternative = useAppMetaStore((s) => s.activeLayoutAlternative);
    const revisionId = useFloorplanDesignStore((s) => s.metaData.revisionId);

    return useMemo(() => {
      if (activeLayoutAlternative.status !== 'loaded') {
        logger.debug(
          'IsLatestRevisionSelector: activeLayoutAlternative is not loaded. Returning false by default.',
          {
            status: activeLayoutAlternative.status,
          },
        );
        return false;
      }

      return IsLatestRevisionSelector.logic(
        activeLayoutAlternative.data.lastRevisionId,
        revisionId,
      );
    }, [activeLayoutAlternative.status, activeLayoutAlternative.data?.lastRevisionId, revisionId]);
  },

  /**
   * @description Non-reactive getter for use where reactivity is not needed
   */
  get: (): boolean => {
    const { activeLayoutAlternative } = useAppMetaStore.getState();
    const {
      metaData: { revisionId },
    } = useFloorplanDesignStore.getState();

    if (activeLayoutAlternative.status !== 'loaded') {
      logger.debug(
        'IsLatestRevisionSelector: activeLayoutAlternative is not loaded. Returning false by default.',
        {
          status: activeLayoutAlternative.status,
        },
      );
      return false;
    }

    return IsLatestRevisionSelector.logic(activeLayoutAlternative.data.lastRevisionId, revisionId);
  },
};
